/** Wire statuses from GET /admin/orders (exact enum values). */
export type OrderStatus =
  | 'PENDING_PAYMENT'
  | 'PAYMENT_EXPIRED'
  | 'CONFIRMED'
  | 'PICKING'
  | 'PARTIALLY_FULFILLED'
  | 'PACKED'
  | 'DISPATCHED'
  | 'DELIVERED'
  | 'CANCELLED'
  | 'RETURNED';

export type OrderCancelReturnAction = 'CANCEL' | 'RETURN';

/** Request body `status` enum for `PATCH /admin/orders/:id/status` (gateway validation). */
export type AdminPatchableOrderStatus =
  | 'PENDING_PAYMENT'
  | 'CONFIRMED'
  | 'PICKING'
  | 'PARTIALLY_FULFILLED'
  | 'PACKED'
  | 'DISPATCHED'
  | 'DELIVERED';

export interface UpdateAdminOrderStatusRequest {
  status: AdminPatchableOrderStatus;
  notes?: string;
}

export interface UpdateAdminOrderStatusData {
  success: boolean;
  message: string;
}

/** List sort modes accepted by admin list API. */
export type OrderListSortBy = 'recent' | 'orderNumber';

export type OrderListSortOrder = 'asc' | 'desc';

/** @deprecated use OrderListSortBy — kept alias for temporary call sites */
export type OrderSort = OrderListSortBy;

export interface OrderRow {
  id: string;
  orderNumber: string;
  customerName: string;
  /** Exact wire status from API */
  status: OrderStatus;
  totalItems: number;
  totalPaise: number;
  originId: string;
  originName: string;
  /** ISO timestamp when delivered — used to gate the 24h return window. */
  deliveredAtIso?: string;
  /** Wire value e.g. PREPAID, COD */
  paymentMethod?: string;
  /** COD collection mode e.g. CASH, QR */
  collectionMode?: string;
  /** Set after an admin continues a pending online payment. */
  pendingPaymentResolution?: PendingPaymentResolution | '';
  /** Admin auth id that continued the pending payment. Empty when unset. */
  pendingPaymentContinuedBy?: string;
  /** ISO timestamp when the pending payment was continued. Empty when unset. */
  pendingPaymentContinuedAt?: string;
}

/** Outcome of `POST /admin/orders/{orderId}/continue-with-cod`. */
export type PendingPaymentResolution = 'SWITCHED_TO_COD' | 'ONLINE_PAYMENT_CAPTURED';

export interface OrderStats {
  totalOrders: number;
  totalDeltaPercent: number;
  /** Backend label e.g. "vs previous period"; may be empty. */
  totalComparisonLabel: string;
  pendingFulfillment: number;
  outForDelivery: number;
  delayed: number;
  delayedDelta: number;
}

export interface OrderSummaryParams {
  storeId?: string;
}

/** Live `GET /admin/orders/summary` payload (normalized camelCase). */
export interface OrderSummary {
  storeId: string;
  dateFrom: string;
  dateTo: string;
  totalOrders: number;
  totalOrdersChangePercent: number;
  totalOrdersComparisonLabel: string;
  pendingFulfillment: number;
  outForDelivery: number;
  delayed: number;
  delayedChange: number;
}

export interface OrdersListParams {
  page?: number;
  limit?: number;
  storeId?: string;
  /** Exact OrderStatus or comma-joined multi-status */
  status?: string;
  orderNumber?: string;
  sortBy?: OrderListSortBy;
  sortOrder?: OrderListSortOrder;
}

/** Statuses shown in the list filter. Pending payment stays so admins can continue those orders. */
export const ORDER_LIST_FILTER_STATUSES: readonly OrderStatus[] = [
  'PENDING_PAYMENT',
  'PAYMENT_EXPIRED',
  'CONFIRMED',
  'PICKING',
  'PARTIALLY_FULFILLED',
  'PACKED',
  'DISPATCHED',
  'DELIVERED',
  'CANCELLED',
  'RETURNED',
] as const;
